import { useMediaQuery } from '@mui/material/'
import { useOktaAuth } from '@okta/okta-react'
import { format } from 'date-fns'
import { useCallback, useEffect } from 'react'
import { isMobile } from 'react-device-detect'
import { useSelector } from 'react-redux'
import {
  setBankHolidays,
  setCalendarDetails,
  setCurrentEntitlementPeriodResponse,
  setDepartmentsResponse,
  setEmployeeDetails,
  setLoggedEmployeeDepartment,
  setLoggedEmployeeTeam,
  setSelectValues,
  setSidebarExpanded,
  setSecondaryApproverEmployees,
  setLeaverEmployees,
} from '../../../redux/reducers/appSettingsReducer'
import actionsService from '../../../services/actionsService'
import { getInitialRequestParams } from '../../../components/MyActionsGrid/myactionsFlitering'
import { normaliseForComparison } from '../../../utils/name-utils'
import { showErrorMessage } from '../../../redux/reducers/snackbarReducer'
import {
  clearLoggedInUser,
  setLoggedInUser,
  setUserPermissions,
} from '../../../redux/reducers/userReducer'
import store, { RootStore, useAppDispatch } from '../../../redux/store'
import { dashboardService } from '../../../services/dashboardService'
import { manualRequestsService } from '../../../services/myActionsService'
import { permissionsService } from '../../../services/permissionsService'
import { isMobileDown, isTabletDown } from '../../../theme/deviceChecks'
import { BaseResponse } from '../../../types/base-response'
import UserErrorMessage from '../../../utils/errorFilter'
import Drawer from '../../UI/Drawer'
import NavigationItemsContainer from './NavigationItemsContainer'
import { ExpandCollapseButton, SidebarPane } from './components'
import { LogoutHelper } from '../../../utils/LogoutHelper'
import {generateClientId, getClientId} from '../../../authentication/clientIdService'
import { refreshFeatureToggles } from '../../../utils/featureToggleProcessor'
import { managerPermisssions, VIEW_WHO_AWAY_MANAGER } from '../../../utils/constants'

export { refreshFeatureToggles }

function Sidebar() {
  const { authState, oktaAuth } = useOktaAuth()
  const mobile = useMediaQuery(isMobileDown())
  const tabletDown = useMediaQuery(isTabletDown())

  const dispatch = useAppDispatch()
  const logoutHelper = new LogoutHelper(oktaAuth, dispatch)

  const expanded = useSelector<RootStore, boolean>(
    (state: RootStore) => state.appSettings.sideBarExpanded
  )

  const { departments, allTeams, employeeDetails } = useSelector(
    (state: RootStore) => state.appSettings
  )

  const dispatchSidebarAction = useCallback(
    (sideBarExpanded: boolean) => {
      dispatch(setSidebarExpanded(sideBarExpanded))
    },
    [dispatch]
  )

  const closeDrawer = useCallback(() => {
    dispatchSidebarAction(false)
  }, [dispatchSidebarAction])

  useEffect(() => {
    if (tabletDown) {
      dispatch(setSidebarExpanded(false))
    } else {
      dispatch(setSidebarExpanded(true))
    }
  }, [dispatch, tabletDown])

  useEffect(() => logoutHelper.oktaExpiryListeners(), [])

  useEffect(() => {
    if (!authState || !authState.isAuthenticated) {
      dispatch(clearLoggedInUser())
    } else {
      oktaAuth.getUser().then(info => {
        dispatch(setLoggedInUser(info))
        generateClientId()
      })

      dashboardService.getEmployeeDetails().then(data => {
        dispatch(setEmployeeDetails(data))
      })

      dashboardService.getSelectValues().then(data => {
        dispatch(setSelectValues(data.selectValues))
      })

      dashboardService.getSelectValuesDepartments().then(data => {
        dispatch(setDepartmentsResponse(data))
      })

      dashboardService.getSelectValuesApprovalSecondaryDepartments().then(data => {
        dispatch(setSecondaryApproverEmployees(data))
      })

      Promise.all([
        dashboardService.getEntitlementPeriodResponse(),
        dashboardService.getCalendarDetails(),
        permissionsService.getUserPermissions(),
      ]).then(([entitlementResponse, calendarResponse, permissionsData]) => {
        dispatch(setCurrentEntitlementPeriodResponse(entitlementResponse))
        dispatch(setCalendarDetails(calendarResponse))
        dispatch(setUserPermissions(permissionsData.permissions))

        const requestParams = getInitialRequestParams(calendarResponse, null, entitlementResponse)
        requestParams!.directReportees = false

        const isManager = permissionsData.permissions.some(permission => 
          managerPermisssions.includes(permission)
        ) && permissionsData.permissions.includes(VIEW_WHO_AWAY_MANAGER)

        if (isManager) {
          Promise.all([
            dashboardService.getLeaverEmployees(),
            actionsService.myActions.getManagerActions(requestParams!),
          ]).then(([leaverEmployees, myActions]) => {
            const myActionsEmployeeNames =
              myActions.requests?.map(r => (r.displayName ? normaliseForComparison(r.displayName) : '')) || []
            const filteredLeaverEmployees = {
              employees: leaverEmployees.employees.filter(e =>
                myActionsEmployeeNames.includes(normaliseForComparison(e.employeeName))
              ),
            }
            dispatch(setLeaverEmployees(filteredLeaverEmployees))
          })
        }
      })
      .catch(err => {
        const response: BaseResponse = err.response.data
        response.errors.forEach(error => {
          dispatch(showErrorMessage(<UserErrorMessage name={error.name} />))
        })
      })

      refreshFeatureToggles()

      const dateMinusOneYear = new Date(new Date().setFullYear(new Date().getFullYear() - 2))
      const datePlusOneYear = new Date(new Date().setFullYear(new Date().getFullYear() + 6))

      manualRequestsService
        .getBankHolidays([
          format(dateMinusOneYear, 'yyyy-MM-dd'),
          format(datePlusOneYear, 'yyyy-MM-dd'),
        ])
        .then(bankHols => {
          dispatch(setBankHolidays(bankHols?.holidays))
        })
    }
  }, [authState, oktaAuth, dispatch])

  useEffect(() => {
    if (!employeeDetails || !departments) {
      return
    }

    const userDepartment = departments.find(dep => dep.value === employeeDetails.departmentId)
    if (!userDepartment) {
      return
    }

    const userTeam = allTeams.find(team => team.teamId === employeeDetails.teamId) ?? null
    dispatch(setLoggedEmployeeDepartment(userDepartment))
    dispatch(setLoggedEmployeeTeam(userTeam))
  }, [allTeams, departments, dispatch, employeeDetails])

  if (!authState || !authState.isAuthenticated) {
    return null
  }

  return (
    <>
      {isMobile || mobile ? (
        <Drawer isOpen={expanded} onClose={closeDrawer} anchor="left" className="mobileNavDrawer">
          <NavigationItemsContainer mobile={isMobile || mobile} closeDrawer={closeDrawer} />
        </Drawer>
      ) : (
        <SidebarPane expanded={expanded} className="desktopNavDrawer">
          {!tabletDown && (
            <ExpandCollapseButton
              expanded={expanded}
              onClick={() => {
                dispatchSidebarAction(!expanded)
              }}
            />
          )}

          <NavigationItemsContainer mobile={mobile} />
        </SidebarPane>
      )}
    </>
  )
}

export default Sidebar
